import React from 'react';
import { createRoot } from 'react-dom/client';
import { Modal, message, notification } from 'antd';

const TOAST_DURATION_SECONDS = 3;
const TOAST_TYPES = {
  success: {
    accent: '#16a34a',
    background: '#f0fdf4',
    border: '#bbf7d0',
    title: 'Success',
  },
  error: {
    accent: '#dc2626',
    background: '#fef2f2',
    border: '#fecaca',
    title: 'Error',
  },
  warning: {
    accent: '#d97706',
    background: '#fffbeb',
    border: '#fde68a',
    title: 'Warning',
  },
  info: {
    accent: '#2563eb',
    background: '#eff6ff',
    border: '#bfdbfe',
    title: 'Info',
  },
  loading: {
    accent: '#4b5563',
    background: '#f9fafb',
    border: '#d1d5db',
    title: 'Loading',
  },
};

const MODAL_TYPES = {
  error: {
    accent: '#dc2626',
    title: 'Error',
  },
  warning: {
    accent: '#d97706',
    title: 'Warning',
  },
  info: {
    accent: '#2563eb',
    title: 'Info',
  },
  success: {
    accent: '#16a34a',
    title: 'Success',
  },
  confirm: {
    accent: '#111827',
    title: 'Confirm',
  },
};

let compatibilityInstalled = false;
let warningFilterInstalled = false;
let hostRoot = null;
let hostContainer = null;
let toastQueue = [];
let modalQueue = [];
let activeToastId = 0;
let activeModalId = 0;

const ensureHost = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  if (!hostContainer) {
    hostContainer = document.createElement('div');
    hostContainer.id = 'antd-react19-compat-host';
    document.body.appendChild(hostContainer);
    hostRoot = createRoot(hostContainer);
  }

  return hostRoot;
};

const rerenderHost = () => {
  const root = ensureHost();

  if (!root) {
    return;
  }

  root.render(
    <CompatHost
      modals={modalQueue}
      toasts={toastQueue}
      onCloseModal={closeModal}
      onCloseToast={closeToast}
      onConfirmModal={confirmModal}
      onCancelModal={cancelModal}
    />
  );
};

const closeToast = (id) => {
  toastQueue = toastQueue.filter((toast) => toast.id !== id);
  rerenderHost();
};

const normalizeToastConfig = (type, args) => {
  const [contentOrConfig, durationArg, onCloseArg] = args;

  if (
    contentOrConfig
    && typeof contentOrConfig === 'object'
    && !React.isValidElement(contentOrConfig)
    && (
      Object.prototype.hasOwnProperty.call(contentOrConfig, 'content')
      || Object.prototype.hasOwnProperty.call(contentOrConfig, 'message')
      || Object.prototype.hasOwnProperty.call(contentOrConfig, 'description')
    )
  ) {
    return {
      type,
      content: contentOrConfig.content ?? contentOrConfig.message,
      description: contentOrConfig.description,
      duration: contentOrConfig.duration,
      onClose: contentOrConfig.onClose,
      key: contentOrConfig.key,
    };
  }

  return {
    type,
    content: contentOrConfig,
    duration: durationArg,
    onClose: onCloseArg,
  };
};

const openToast = (type, ...args) => {
  const toastConfig = normalizeToastConfig(type, args);
  const id = toastConfig.key ?? `toast-${++activeToastId}`;
  const durationSeconds = toastConfig.duration ?? TOAST_DURATION_SECONDS;

  toastQueue = toastQueue.filter((toast) => toast.id !== id);
  toastQueue = [
    ...toastQueue,
    {
      id,
      type: toastConfig.type,
      content: toastConfig.content,
      description: toastConfig.description,
      onClose: toastConfig.onClose,
    },
  ];
  rerenderHost();

  if (durationSeconds !== 0) {
    window.setTimeout(() => {
      const toast = toastQueue.find((entry) => entry.id === id);

      if (!toast) {
        return;
      }

      if (typeof toast.onClose === 'function') {
        toast.onClose();
      }

      closeToast(id);
    }, durationSeconds * 1000);
  }

  return () => closeToast(id);
};

const closeModal = (id) => {
  modalQueue = modalQueue.filter((modalConfig) => modalConfig.id !== id);
  rerenderHost();
};

const confirmModal = (id) => {
  const modalConfig = modalQueue.find((entry) => entry.id === id);

  if (!modalConfig) {
    return;
  }

  if (typeof modalConfig.onOk === 'function') {
    modalConfig.onOk();
  }

  closeModal(id);
};

const cancelModal = (id) => {
  const modalConfig = modalQueue.find((entry) => entry.id === id);

  if (!modalConfig) {
    return;
  }

  if (typeof modalConfig.onCancel === 'function') {
    modalConfig.onCancel();
  }

  closeModal(id);
};

const openModal = (type, config = {}) => {
  const id = config.key ?? `modal-${++activeModalId}`;

  modalQueue = modalQueue.filter((modalConfig) => modalConfig.id !== id);
  modalQueue = [
    ...modalQueue,
    {
      id,
      type,
      title: config.title,
      content: config.content,
      okText: config.okText,
      cancelText: config.cancelText,
      onOk: config.onOk,
      onCancel: config.onCancel,
      closable: config.closable !== false,
      showCancel: type === 'confirm' || typeof config.onCancel === 'function',
      width: config.width,
    },
  ];
  rerenderHost();

  return {
    destroy: () => closeModal(id),
    update: (nextConfig) => {
      modalQueue = modalQueue.map((modalConfig) => (
        modalConfig.id === id ? { ...modalConfig, ...nextConfig } : modalConfig
      ));
      rerenderHost();
    },
  };
};

const patchMessageApi = () => {
  message.open = (config) => openToast(config?.type ?? 'info', config);
  message.success = (...args) => openToast('success', ...args);
  message.error = (...args) => openToast('error', ...args);
  message.warning = (...args) => openToast('warning', ...args);
  message.info = (...args) => openToast('info', ...args);
  message.loading = (...args) => openToast('loading', ...args);
  message.destroy = (key) => {
    if (key) {
      closeToast(key);
      return;
    }

    toastQueue = [];
    rerenderHost();
  };
  message.config = () => {};
};

const patchNotificationApi = () => {
  const openNotification = (type, config = {}) => openToast(type, {
    key: config.key,
    message: config.message,
    description: config.description,
    duration: config.duration,
    onClose: config.onClose,
  });

  notification.open = (config = {}) => openNotification(config.type ?? 'info', config);
  notification.success = (config = {}) => openNotification('success', config);
  notification.error = (config = {}) => openNotification('error', config);
  notification.warning = (config = {}) => openNotification('warning', config);
  notification.info = (config = {}) => openNotification('info', config);
  notification.destroy = (key) => {
    if (key) {
      closeToast(key);
      return;
    }

    toastQueue = [];
    rerenderHost();
  };
  notification.close = (key) => closeToast(key);
  notification.config = () => {};
};

const patchModalApi = () => {
  Modal.error = (config) => openModal('error', config);
  Modal.warning = (config) => openModal('warning', config);
  Modal.info = (config) => openModal('info', config);
  Modal.success = (config) => openModal('success', config);
  Modal.confirm = (config) => openModal('confirm', config);
  Modal.destroyAll = () => {
    modalQueue = [];
    rerenderHost();
  };
};

const ToastCard = ({ toast, onClose }) => {
  const typeConfig = TOAST_TYPES[toast.type] || TOAST_TYPES.info;

  return (
    <div
      style={{
        width: 360,
        border: `1px solid ${typeConfig.border}`,
        background: typeConfig.background,
        borderLeft: `4px solid ${typeConfig.accent}`,
        borderRadius: 8,
        boxShadow: '0 10px 25px rgba(15, 23, 42, 0.12)',
        padding: '12px 14px',
        marginBottom: 12,
        color: '#111827',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            {toast.content ? null : typeConfig.title}
          </div>
          {
            toast.content && (
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: toast.description ? 4 : 0 }}>
                {toast.content}
              </div>
            )
          }
          {
            toast.description && (
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                {toast.description}
              </div>
            )
          }
        </div>
        <button
          type="button"
          onClick={() => onClose(toast.id)}
          style={{
            border: 0,
            background: 'transparent',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
        >
          x
        </button>
      </div>
    </div>
  );
};

const CompatModal = ({ modalConfig, onCancel, onConfirm }) => {
  const typeConfig = MODAL_TYPES[modalConfig.type] || MODAL_TYPES.info;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10010,
        padding: 24,
      }}
    >
      <div
        style={{
          width: modalConfig.width ?? 416,
          maxWidth: '100%',
          background: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            borderTop: `4px solid ${typeConfig.accent}`,
            padding: '20px 24px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              {modalConfig.title || typeConfig.title}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
              {modalConfig.content}
            </div>
          </div>
          {
            modalConfig.closable && (
              <button
                type="button"
                onClick={() => onCancel(modalConfig.id)}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                x
              </button>
            )
          }
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            padding: '0 24px 24px',
          }}
        >
          {
            modalConfig.showCancel && (
              <button
                type="button"
                onClick={() => onCancel(modalConfig.id)}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  background: '#ffffff',
                  color: '#111827',
                  cursor: 'pointer',
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {modalConfig.cancelText || 'Cancel'}
              </button>
            )
          }
          <button
            type="button"
            onClick={() => onConfirm(modalConfig.id)}
            style={{
              border: 0,
              borderRadius: 8,
              background: typeConfig.accent,
              color: '#ffffff',
              cursor: 'pointer',
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {modalConfig.okText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CompatHost = ({ modals, onCancelModal, onCloseModal, onCloseToast, onConfirmModal, toasts }) => (
  <>
    <div
      style={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 10020,
        pointerEvents: 'none',
      }}
    >
      <div style={{ pointerEvents: 'auto' }}>
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onClose={onCloseToast} />
        ))}
      </div>
    </div>
    {modals.map((modalConfig) => (
      <CompatModal
        key={modalConfig.id}
        modalConfig={modalConfig}
        onCancel={modalConfig.showCancel ? onCancelModal : onCloseModal}
        onConfirm={onConfirmModal}
      />
    ))}
  </>
);

export const installAntdStaticCompat = () => {
  if (compatibilityInstalled) {
    return;
  }

  compatibilityInstalled = true;
  patchMessageApi();
  patchNotificationApi();
  patchModalApi();
  rerenderHost();
};

export const installKnownReact19WarningFilter = () => {
  if (warningFilterInstalled || process.env.NODE_ENV !== 'development') {
    return;
  }

  warningFilterInstalled = true;

  const originalConsoleError = console.error;

  console.error = (...args) => {
    const serializedArgs = args
      .map((arg) => (typeof arg === 'string' ? arg : ''))
      .join(' ');

    if (serializedArgs.includes('Accessing element.ref was removed in React 19')) {
      return;
    }

    originalConsoleError(...args);
  };
};
